// src/components/ChessBoard.tsx (Production Version - Debug Removed)
import React from "react";
import { Position, Piece } from "../types/Board.ts";
import {
  getSquareKey,
  getPieceAt,
  isLightSquare,
  getFileLabel,
  getRankLabel,
} from "../utils/gameHelpers.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";

interface ChessBoardProps {
  pieces: Map<string, Piece>;
  boardRows: number;
  boardCols: number;
  selectedSquare: Position | null;
  highlightedMoves: Position[];
  opponentHighlightedMoves?: Position[];
  lightSquareColor: string;
  darkSquareColor: string;
  onSquareClick: (row: number, col: number) => void;
  isReviewingHistory: boolean;
  gameStatus: "active" | "check" | "checkmate" | "stalemate";
  isAiThinking?: boolean;
  disabled?: boolean;
}

export const ChessBoard: React.FC<ChessBoardProps> = ({
  pieces,
  boardRows,
  boardCols,
  selectedSquare,
  highlightedMoves,
  opponentHighlightedMoves = [],
  lightSquareColor,
  darkSquareColor,
  onSquareClick,
  isReviewingHistory,
  gameStatus,
  isAiThinking = false,
  disabled = false,
}) => {
  const isHighlighted = (row: number, col: number): boolean => {
    return highlightedMoves.some((pos) => pos.row === row && pos.col === col);
  };

  const isOpponentHighlighted = (row: number, col: number): boolean => {
    return opponentHighlightedMoves.some((pos) => pos.row === row && pos.col === col);
  };

  const isSelected = (row: number, col: number): boolean => {
    return selectedSquare?.row === row && selectedSquare?.col === col;
  };

  const getSquareClassName = (row: number, col: number): string => {
    const classes = ["square"];
    classes.push(isLightSquare(row, col) ? "light" : "dark");
    if (isSelected(row, col)) classes.push("selected");
    if (isHighlighted(row, col)) classes.push("highlighted");
    return classes.join(" ");
  };

  const handleSquareClick = (row: number, col: number) => {
    if (
      disabled ||
      isReviewingHistory ||
      isAiThinking ||
      gameStatus === "checkmate" ||
      gameStatus === "stalemate"
    ) {
      return;
    }
    onSquareClick(row, col);
  };

  return (
    <div className="board-center-container">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div className="board-wrapper">
          {/* Rank Labels (left side) */}
          <div className="rank-labels">
            {Array.from({ length: boardRows }, (_, i) => (
              <div key={i} className="rank-label">
                {getRankLabel(i, boardRows)}
              </div>
            ))}
          </div>

          {/* Chess Board */}
          <div
            className="chess-board"
            style={{
              gridTemplateColumns: `repeat(${boardCols}, 1fr)`,
              gridTemplateRows: `repeat(${boardRows}, 1fr)`,
            }}
          >
            {Array.from({ length: boardRows }, (_, row) =>
              Array.from({ length: boardCols }, (_, col) => {
                const piece = getPieceAt(row, col, pieces);
                const highlighted = isHighlighted(row, col);
                const opponentHighlighted = isOpponentHighlighted(row, col);

                return (
                  <div
                    key={getSquareKey(row, col)}
                    className={getSquareClassName(row, col)}
                    style={{
                      backgroundColor: isLightSquare(row, col)
                        ? lightSquareColor
                        : darkSquareColor,
                      position: "relative",
                      cursor:
                        disabled || isReviewingHistory || isAiThinking
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onClick={() => handleSquareClick(row, col)}
                  >
                    {/* Highlight Overlay (player's moves - green dot centered) */}
                    {highlighted && !piece && (
                      <div
                        style={{
                          position: "absolute",
                          width: "30%",
                          height: "30%",
                          backgroundColor: "rgba(0, 180, 0, 0.8)",
                          borderRadius: "50%",
                          border: "2px solid rgba(0, 0, 0, 0.8)",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      />
                    )}
                    {/* Capture indicator (player's moves - green X centered) */}
                    {highlighted && piece && (
                      <div
                        style={{
                          position: "absolute",
                          width: "35%",
                          height: "35%",
                          backgroundColor: "rgba(100, 200, 100, 0.9)",
                          border: "2px solid rgba(0, 0, 0, 0.8)",
                          borderRadius: "3px",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                          zIndex: 3,
                        }}
                      >
                        <span style={{
                          color: "#000",
                          fontWeight: "bold",
                          fontSize: "14px",
                          lineHeight: 1,
                        }}>✕</span>
                      </div>
                    )}

                    {/* Opponent Highlight Overlay (opponent's moves - red dot centered) */}
                    {opponentHighlighted && !piece && (
                      <div
                        style={{
                          position: "absolute",
                          width: "30%",
                          height: "30%",
                          backgroundColor: "rgba(255, 0, 0, 0.8)",
                          borderRadius: "50%",
                          border: "2px solid rgba(0, 0, 0, 0.8)",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      />
                    )}
                    {/* Capture indicator (opponent's moves - red X centered) */}
                    {opponentHighlighted && piece && (
                      <div
                        style={{
                          position: "absolute",
                          width: "35%",
                          height: "35%",
                          backgroundColor: "rgba(255, 100, 100, 0.9)",
                          border: "2px solid rgba(0, 0, 0, 0.8)",
                          borderRadius: "3px",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                          zIndex: 3,
                        }}
                      >
                        <span style={{
                          color: "#000",
                          fontWeight: "bold",
                          fontSize: "14px",
                          lineHeight: 1,
                        }}>✕</span>
                      </div>
                    )}

                    {/* Piece Image */}
                    {piece && (
                      <div
                        className="piece"
                        style={{ position: "relative", zIndex: 2 }}
                      >
                        <img
                          src={`/pieces/${(piece.abbreviation || PIECE_DEFINITIONS[piece.type.toLowerCase()]?.abbreviation || 'pwn').toLowerCase()}.png`}
                          alt={piece.type}
                          className={`piece-image ${
                            piece.color === "white"
                              ? "white-piece"
                              : "black-piece"
                          }`}
                          draggable={false}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            pointerEvents: "none",
                          }}
                          onError={(e) => {
                            console.error(`Failed to load image for piece: ${piece.type}, abbreviation: ${piece.abbreviation}`);
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* File Labels (bottom) */}
        <div className="file-labels-bottom">
          {Array.from({ length: boardCols }, (_, i) => (
            <div key={i} className="file-label-bottom">
              {getFileLabel(i)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChessBoard;
